import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { parse, PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError, isNetworkError } from '../../util/errors';
import { isUserAuthorized } from '../../util/userHelpers';
import { retrieveUserWishlist } from '../../util/userDataExtractor';
import { isArrayLength } from '../../util/genericHelpers';
import { resultInfiniteScrollIds, searchListingConfig } from '../../util/searchPageHelpers';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { isOriginInUse } from '../../util/search';
import { SharetribeApi } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
import { filterOutTutulistListings, stripTutulistEncoreQueryParam } from '../../util/tutulist';
import * as log from '../../util/log';
import { withPrivateListingsExcluded } from '../../util/publicListingQuery';
import { getActiveListingTypes, pickSearchParamsOnly } from '../SearchPage/SearchPage.shared';
import { resolveListingSort } from '../../util/listingSort';

const { UUID, Money } = sdkTypes;

const RESULT_PAGE_SIZE = 100;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const stripTransitMapKeyPrefix = key =>
  typeof key === 'string' && key.startsWith('~:') ? key.slice(2) : key;

const stripTransitUuidPrefix = value =>
  typeof value === 'string' && value.startsWith('~u') ? value.slice(2) : value;

const isPlainObject = value => {
  if (!value || Object.prototype.toString.call(value) !== '[object Object]') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const toMinorUnitAmount = rawAmount => {
  if (typeof rawAmount === 'number' && Number.isFinite(rawAmount)) {
    return Math.trunc(rawAmount);
  }

  if (typeof rawAmount === 'string') {
    const trimmed = rawAmount.trim();
    if (/^-?\d+$/.test(trimmed)) {
      return Number(trimmed);
    }
    return null;
  }

  if (!rawAmount || typeof rawAmount !== 'object') {
    return null;
  }

  if (typeof rawAmount?.value !== 'undefined') {
    const nestedValue = toMinorUnitAmount(rawAmount.value);
    if (nestedValue !== null) {
      return nestedValue;
    }
  }

  if (typeof rawAmount?.toNumber === 'function') {
    const converted = rawAmount.toNumber();
    if (typeof converted === 'number' && Number.isFinite(converted)) {
      return Math.trunc(converted);
    }
  }

  if (typeof rawAmount?.toString === 'function') {
    const asString = rawAmount.toString();
    if (/^-?\d+$/.test(`${asString}`.trim())) {
      return Number(asString);
    }
  }

  if (Number.isFinite(rawAmount.low) && Number.isFinite(rawAmount.high)) {
    const low = rawAmount.low >>> 0;
    const high = rawAmount.high | 0;
    return high * 4294967296 + low;
  }

  return null;
};

const toMoneyIfPossible = value => {
  if (!isPlainObject(value)) {
    return value;
  }

  if (value instanceof Money) {
    return value;
  }

  const transitMoneyValue = value['~#mn'] || value['#mn'];
  if (Array.isArray(transitMoneyValue) && transitMoneyValue.length === 2) {
    const [amountRaw, currencyRaw] = transitMoneyValue;
    const amount = toMinorUnitAmount(amountRaw);
    if (amount !== null && typeof currencyRaw === 'string') {
      return new Money(amount, currencyRaw);
    }
  }

  const hasPlainMoneyShape =
    Object.prototype.hasOwnProperty.call(value, 'amount') &&
    Object.prototype.hasOwnProperty.call(value, 'currency') &&
    Object.keys(value).every(key => ['amount', 'currency'].includes(key));
  if (hasPlainMoneyShape) {
    const amount = toMinorUnitAmount(value.amount);
    if (amount !== null && typeof value.currency === 'string') {
      return new Money(amount, value.currency);
    }
  }

  return value;
};

const toUuidIfPossible = value => {
  const maybeUuid = stripTransitUuidPrefix(value);
  if (typeof maybeUuid !== 'string' || !UUID_PATTERN.test(maybeUuid)) {
    return value;
  }

  try {
    return new UUID(maybeUuid);
  } catch (e) {
    return { uuid: maybeUuid };
  }
};

const normalizeTransitLikePayload = value => {
  if (Array.isArray(value)) {
    return value.map(item => normalizeTransitLikePayload(item));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  if (value instanceof UUID || value instanceof Money) {
    return value;
  }

  const moneyMaybe = toMoneyIfPossible(value);
  if (moneyMaybe !== value) {
    return moneyMaybe;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const normalized = Object.entries(value).reduce((acc, [rawKey, rawValue]) => {
    const key = stripTransitMapKeyPrefix(rawKey);
    acc[key] = normalizeTransitLikePayload(rawValue);
    return acc;
  }, {});

  if (typeof normalized?.id === 'string') {
    normalized.id = toUuidIfPossible(normalized.id);
  }

  return normalized;
};

const normalizeEntityId = id => {
  if (!id) {
    return null;
  }
  if (typeof id === 'object' && id?.uuid) {
    return id;
  }

  const parsed = toUuidIfPossible(id);
  return typeof parsed === 'object' && parsed?.uuid ? parsed : null;
};

const normalizeSoldItemsResponse = rawResponse => {
  if (typeof rawResponse === 'string') {
    try {
      const parsedResponse = JSON.parse(rawResponse);
      return normalizeTransitLikePayload(parsedResponse);
    } catch (e) {
      return normalizeTransitLikePayload(rawResponse);
    }
  }

  return normalizeTransitLikePayload(rawResponse);
};

const resultIds = data => {
  const listings = data?.data || [];
  return listings.map(l => l.id);
};

const normalizeApiError = error => {
  if (!error) {
    return error;
  }
  const status = error.status ?? error.statusCode;
  const statusText = error.statusText ?? error.message;
  return { ...error, status, statusText };
};

const getUserIdFromServerLookup = async username => {
  const response = await SharetribeApi.fetchUserIdByUsername(username);
  const statusCode = Number(response?.statusCode || response?.status || 0);
  const userId = response?.data?.userId;

  if (statusCode === 200 && userId) {
    return new UUID(userId);
  }

  return null;
};

const normalizeStoreParams = ({ params = {}, config, currentPathParams = {} }) => {
  const sanitizedParams = stripTutulistEncoreQueryParam(params || {});
  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
  const { listingType: listingTypePathParam } = currentPathParams;
  const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
  const listingCategories = config?.categoryConfiguration?.categories || [];

  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams,
  };

  const pickedParams = pickSearchParamsOnly(
    sanitizedParams,
    filterConfigs,
    sortConfig,
    isOriginInUse(config)
  );

  const trimmedKeywords = `${pickedParams?.keywords || ''}`.trim();
  const keywordsMaybe = trimmedKeywords ? { keywords: trimmedKeywords } : {};
  const sort = pickedParams?.sort;
  const sortMaybe = sort ? { sort } : {};

  return {
    ...pickedParams,
    ...keywordsMaybe,
    ...sortMaybe,
  };
};

export const buildStoreListingQuery = ({
  config,
  page,
  rawParams,
  currentPathParams,
  userId,
  wishlistIds,
}) => {
  const normalizedParams = normalizeStoreParams({
    params: rawParams,
    config,
    currentPathParams,
  });

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  const imageParams = searchListingConfig(variantPrefix, aspectRatio);

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? `${priceParam}`.split(',') : [];
    return values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const { price, sort, ...rest } = normalizedParams;
  const priceMaybe = priceSearchParams(price);
  const clampedWishlistIds = Array.isArray(wishlistIds) ? wishlistIds.slice(0, 100) : [];
  const queryingWishlist = isArrayLength(clampedWishlistIds);
  const resolvedSort =
    queryingWishlist || sort === config?.search?.sortConfig?.relevanceKey
      ? null
      : resolveListingSort(sort, { hasKeywords: !!rest.keywords });
  const sortMaybe = resolvedSort ? { sort: resolvedSort } : {};
  const ownerFilter = queryingWishlist ? { ids: clampedWishlistIds } : { authorId: userId };

  return withPrivateListingsExcluded({
    ...rest,
    ...ownerFilter,
    ...priceMaybe,
    ...sortMaybe,
    page,
    perPage: RESULT_PAGE_SIZE,
    ...imageParams,
    minStock: 1,
    include: ['author', 'images', 'currentStock'],
  });
};

const summarizeStorePageContext = ({
  currentPathParams = {},
  fetchWishlist = false,
  page = null,
  params = {},
  userId = null,
  username = null,
}) => {
  const filterKeys = Object.keys(params || {})
    .filter(key => {
      const value = params?.[key];
      return value !== null && typeof value !== 'undefined' && value !== '';
    })
    .slice(0, 20);

  return {
    fetchWishlist: Boolean(fetchWishlist),
    filterKeys,
    page: Number(page || params?.page || 1) || 1,
    profileId: userId?.uuid || userId || null,
    routeName: 'StorePage',
    sort: params?.sort || null,
    username: username || currentPathParams?.username || null,
  };
};

// ================ Async Thunks ================ //

///////////////
// Show User //
///////////////
const showUserPayloadCreator = ({ userId, config }, { dispatch, rejectWithValue, extra: sdk }) => {
  const MAX_RETRIES = 2;
  const showWithRetry = async retriesLeft => {
    try {
      return await sdk.users.show({
        id: userId,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      });
    } catch (e) {
      if (retriesLeft > 0 && isNetworkError(e)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return showWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  return showWithRetry(MAX_RETRIES)
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      const isNetError = isNetworkError(e);
      const logFn = isNetError ? log.warn : log.error;
      logFn(e, 'store-page-user-show-failed', {
        profileId: userId?.uuid || userId || null,
      });
      return rejectWithValue(storableError(e));
    });
};

export const showUserThunk = createAsyncThunk('StorePage/showUser', showUserPayloadCreator);

export const showUser = (userId, config) => dispatch => {
  return dispatch(showUserThunk({ userId, config }));
};

/////////////////////////
// Query User Listings //
/////////////////////////
const queryUserListingsPayloadCreator = async (
  {
    profileUser,
    params = {},
    userId,
    config,
    currentPathParams = {},
    isInfiniteScroll = false,
    fetchWishlist = false,
    page,
  },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const requestedPage = Number(page || params?.page || 1) || 1;
  const normalizedParams = normalizeStoreParams({
    params,
    config,
    currentPathParams,
  });

  const favorites = fetchWishlist ? retrieveUserWishlist(profileUser) || [] : [];
  const wishlistIds = isArrayLength(favorites) ? favorites : [];

  if (fetchWishlist && !wishlistIds.length) {
    return {
      listings: [],
      response: {
        data: {
          data: [],
          meta: {
            page: requestedPage,
            perPage: RESULT_PAGE_SIZE,
            totalItems: 0,
            totalPages: 0,
          },
        },
      },
      isInfiniteScroll,
      requestedPage,
      queryParams: normalizedParams,
      fetchWishlist,
    };
  }

  const queryParams = buildStoreListingQuery({
    config,
    page: requestedPage,
    rawParams: params,
    currentPathParams,
    userId,
    wishlistIds: fetchWishlist ? wishlistIds : null,
  });

  const MAX_RETRIES = 2;
  const queryWithRetry = async retriesLeft => {
    try {
      return await sdk.listings.query(queryParams);
    } catch (e) {
      if (retriesLeft > 0 && isNetworkError(e)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  return queryWithRetry(MAX_RETRIES)
    .then(response => {
      const listings = filterOutTutulistListings(response?.data?.data || []);

      const payload = {
        ...response,
        data: {
          ...response.data,
          data: listings,
        },
      };

      dispatch(addMarketplaceEntities(payload));

      return {
        listings,
        response: payload,
        isInfiniteScroll,
        requestedPage,
        queryParams: normalizedParams,
        fetchWishlist,
      };
    })
    .catch(e => {
      const isNetError = isNetworkError(e);
      const logFn = isNetError ? log.warn : log.error;
      logFn(e, 'store-page-listings-query-failed', {
        ...summarizeStorePageContext({
          currentPathParams,
          fetchWishlist,
          page: requestedPage,
          params: normalizedParams,
          userId,
        }),
      });
      return rejectWithValue(storableError(e));
    });
};

export const queryUserListingsThunk = createAsyncThunk(
  'StorePage/queryUserListings',
  queryUserListingsPayloadCreator
);

export const queryUserListings = ({
  profileUser,
  params = {},
  userId,
  config,
  currentPathParams = {},
  isInfiniteScroll = false,
  fetchWishlist = false,
  page,
}) => async dispatch => {
  return dispatch(
    queryUserListingsThunk({
      profileUser,
      params,
      userId,
      config,
      currentPathParams,
      isInfiniteScroll,
      fetchWishlist,
      page,
    })
  )
    .unwrap()
    .then(payload => payload.listings);
};

//////////////////////////
// Query User's Reviews //
//////////////////////////
const queryUserReviewsPayloadCreator = ({ userId }, { rejectWithValue, extra: sdk }) => {
  const subjectId = typeof userId === 'string' ? new UUID(userId) : userId;
  const MAX_RETRIES = 2;
  const queryWithRetry = async retriesLeft => {
    try {
      return await sdk.reviews.query({
        subject_id: subjectId,
        type: 'ofProvider',
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      });
    } catch (e) {
      if (retriesLeft > 0 && isNetworkError(e)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  return queryWithRetry(MAX_RETRIES)
    .then(response => {
      return denormalisedResponseEntities(response);
    })
    .catch(e => {
      const isNetError = isNetworkError(e);
      const logFn = isNetError ? log.warn : log.error;
      logFn(e, 'store-page-reviews-query-failed', {
        profileId: userId?.uuid || userId || null,
        routeName: 'StorePage',
      });
      return rejectWithValue(storableError(e));
    });
};

export const queryUserReviewsThunk = createAsyncThunk(
  'StorePage/queryUserReviews',
  queryUserReviewsPayloadCreator
);

export const queryUserReviews = userId => dispatch => {
  return dispatch(queryUserReviewsThunk({ userId }));
};

///////////////////////////////
// Get User ID by Username //
///////////////////////////////
const getUserIdPayloadCreator = async ({ username }, { rejectWithValue }) => {
  const normalizedUsername = `${username || ''}`.trim();
  if (!normalizedUsername) {
    log.warn(
      new Error('Store page username lookup was attempted without a username'),
      'store-page-username-missing'
    );
    return rejectWithValue(
      storableError({
        status: 400,
        statusText: 'Username is required',
        message: 'Username is required',
      })
    );
  }

  // The server finds the user whatever the case of the link ("mariav" for "MariaV")
  let lookupError = null;
  try {
    const userId = await getUserIdFromServerLookup(normalizedUsername);
    if (userId) {
      return userId;
    }
  } catch (error) {
    lookupError = normalizeApiError(error);
  }

  // Only a real "not found" answer means the shop doesn't exist. Any other failure (including a
  // timeout with no status) is an error, so the page isn't served as a 404 by mistake.
  if (lookupError && lookupError?.status !== 404) {
    log.error(lookupError, 'store-page-user-id-lookup-failed', {
      routeName: 'StorePage',
      username: normalizedUsername,
    });
    return rejectWithValue(storableError(lookupError));
  }

  return rejectWithValue(
    storableError({ status: 404, statusText: 'User not found', message: 'User not found' })
  );
};

export const getUserIdThunk = createAsyncThunk('StorePage/getUserId', getUserIdPayloadCreator);

export const getUserIdUsingIsdk = username => dispatch => {
  return dispatch(getUserIdThunk({ username })).then(result => {
    if (getUserIdThunk.fulfilled.match(result)) {
      return result.payload;
    }
    return null;
  });
};

////////////////////////
// Fetch Sold Items //
////////////////////////
const fetchSoldItemsPayloadCreator = async ({ userId }, { dispatch, rejectWithValue }) => {
  try {
    const rawResponse = await SharetribeApi.fetchSoldItems(userId);
    const response = normalizeSoldItemsResponse(rawResponse);
    const status = Number(response?.status ?? response?.statusCode ?? 200);
    if (status < 200 || status >= 300) {
      return rejectWithValue(storableError({ status, statusText: 'Failed to fetch sold items' }));
    }

    const soldData = response?.data?.soldListings
      ? response?.data
      : response?.data?.data?.soldListings
      ? response?.data?.data
      : response;
    const soldListingsResponse = soldData?.soldListings || [];
    const totalSold = Number(soldData?.totalSold || 0);

    const flattenedListings =
      (isArrayLength(soldListingsResponse)
        ? soldListingsResponse
            .map(item => {
              if (item?.data?.data) {
                item.data.data = filterOutTutulistListings(item.data.data);
              }
              if (item?.data) {
                dispatch(addMarketplaceEntities(item));
              }
              return item?.data?.data || [];
            })
            .flat()
        : []) || [];

    const filteredSoldListings = filterOutTutulistListings(flattenedListings);
    const seenListingIds = new Set();
    const uniqueSoldListings = filteredSoldListings.filter(listing => {
      const id = listing?.id?.uuid || stripTransitUuidPrefix(listing?.id);
      if (!id || seenListingIds.has(id)) {
        return false;
      }
      seenListingIds.add(id);
      return true;
    });

    const soldItemsFromListings = filteredSoldListings.reduce((acc, listing) => {
      const soldStock = Number(listing?.attributes?.soldStock || 0);
      return acc + (Number.isFinite(soldStock) && soldStock > 0 ? soldStock : 1);
    }, 0);
    const finalTotalSold = totalSold > 0 ? totalSold : soldItemsFromListings;

    return {
      totalItems: finalTotalSold,
      soldListings: uniqueSoldListings,
    };
  } catch (error) {
    log.error(error, 'store-page-sold-items-fetch-failed', {
      profileId: userId?.uuid || userId || null,
      routeName: 'StorePage',
    });
    return rejectWithValue(storableError(error));
  }
};

export const fetchSoldItemsThunk = createAsyncThunk(
  'StorePage/fetchSoldItems',
  fetchSoldItemsPayloadCreator
);

export const fetchSoldItemsUsingIsdk = userId => dispatch => {
  return dispatch(fetchSoldItemsThunk({ userId }));
};

// ================ Slice ================ //

const initialState = {
  userId: null,
  userShowError: null,
  queryListingsInProgress: false,
  isFetchingNextPage: false,
  queryListingsError: null,
  queryParams: {},
  reviews: [],
  queryReviewsInProgress: false,
  queryReviewsError: null,
  queryUserIdInProgress: false,
  queryUserIdError: null,
  soldItems: 0,
  soldListings: [],
  hasFetchedSoldItems: false,
  fetchSoldItemsInProgress: false,
  fetchSoldItemsError: null,
  pagination: null,
  currentPageResultIds: [],
};

const storePageSlice = createSlice({
  name: 'StorePage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // showUser
      .addCase(showUserThunk.pending, (state, action) => {
        state.userShowError = null;
        state.userId = action.meta.arg.userId;
      })
      .addCase(showUserThunk.fulfilled, state => {
        return state;
      })
      .addCase(showUserThunk.rejected, (state, action) => {
        state.userShowError = storableError(action.payload || action.error);
      })

      // queryUserListings
      .addCase(queryUserListingsThunk.pending, (state, action) => {
        const { isInfiniteScroll } = action.meta.arg;
        state.queryListingsError = null;
        if (isInfiniteScroll) {
          state.isFetchingNextPage = true;
          return;
        }

        state.queryListingsInProgress = true;
        state.isFetchingNextPage = false;
        state.currentPageResultIds = [];
      })
      .addCase(queryUserListingsThunk.fulfilled, (state, action) => {
        const { response, isInfiniteScroll, queryParams } = action.payload;
        const incomingIds = resultIds(response?.data);

        state.currentPageResultIds = isInfiniteScroll
          ? resultInfiniteScrollIds(response?.data, state.currentPageResultIds)
          : incomingIds;
        state.pagination = response?.data?.meta || null;
        state.queryParams = queryParams || {};
        state.queryListingsInProgress = false;
        state.isFetchingNextPage = false;
      })
      .addCase(queryUserListingsThunk.rejected, (state, action) => {
        const { isInfiniteScroll } = action.meta.arg;
        state.queryListingsError = storableError(action.payload || action.error);
        state.queryListingsInProgress = false;
        state.isFetchingNextPage = false;

        if (!isInfiniteScroll) {
          state.currentPageResultIds = [];
          state.pagination = null;
        }
      })

      // queryUserReviews
      .addCase(queryUserReviewsThunk.pending, state => {
        state.queryReviewsError = null;
        state.queryReviewsInProgress = true;
      })
      .addCase(queryUserReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
        state.queryReviewsInProgress = false;
      })
      .addCase(queryUserReviewsThunk.rejected, (state, action) => {
        state.reviews = [];
        state.queryReviewsError = storableError(action.payload || action.error);
        state.queryReviewsInProgress = false;
      })

      // getUserId
      .addCase(getUserIdThunk.pending, state => {
        state.queryUserIdError = null;
        state.queryUserIdInProgress = true;
      })
      .addCase(getUserIdThunk.fulfilled, state => {
        state.queryUserIdInProgress = false;
      })
      .addCase(getUserIdThunk.rejected, (state, action) => {
        state.queryUserIdError = storableError(action.payload || action.error);
        state.queryUserIdInProgress = false;
      })

      // fetchSoldItems
      .addCase(fetchSoldItemsThunk.pending, state => {
        state.soldItems = 0;
        state.soldListings = [];
        state.hasFetchedSoldItems = true;
        state.fetchSoldItemsInProgress = true;
        state.fetchSoldItemsError = null;
      })
      .addCase(fetchSoldItemsThunk.fulfilled, (state, action) => {
        state.soldItems = action.payload?.totalItems || 0;
        state.soldListings =
          action.payload?.soldListings?.map(item => normalizeEntityId(item?.id))?.filter(Boolean) ||
          [];
        state.fetchSoldItemsInProgress = false;
      })
      .addCase(fetchSoldItemsThunk.rejected, (state, action) => {
        state.fetchSoldItemsError = storableError(action.payload || action.error);
        state.hasFetchedSoldItems = true;
        state.fetchSoldItemsInProgress = false;
      });
  },
});

export const { setInitialState, setUserId } = storePageSlice.actions;
export default storePageSlice.reducer;

const isCurrentUser = (userId, currentUser) => userId?.uuid === currentUser?.id?.uuid;

// ================ Load More Listings ================ //

export const loadMoreListings = params => dispatch => {
  const { config, currentPathParams = {}, page, search = '', ...rest } = params;
  const parsedQueryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const requestedPage = Number(page || parsedQueryParams.page || 1) || 1;

  return dispatch(
    queryUserListings({
      ...rest,
      config,
      currentPathParams,
      params: { ...parsedQueryParams, page: requestedPage },
      page: requestedPage,
      isInfiniteScroll: true,
    })
  );
};

// ================ Load Data ================ //

export const loadData = (params, search, config) => async (dispatch, getState) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const username = params?.username;
  const currentUser = getState()?.user?.currentUser;
  let userId;
  if (username) {
    userId = await dispatch(getUserIdUsingIsdk(username));
  }

  if (!userId) {
    return Promise.resolve();
  }

  const isPreviewForCurrentUser = params.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  if (isPreviewForCurrentUser) {
    return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
      if (isCurrentUser(userId, currentUser) && isUserAuthorized(currentUser)) {
        return Promise.all([
          dispatch(showUser(userId, config)),
          dispatch(
            queryUserListings({
              params: queryParams,
              userId,
              config,
              currentPathParams: params,
            })
          ),
          dispatch(queryUserReviews(userId)),
          dispatch(fetchSoldItemsUsingIsdk(userId?.uuid)),
        ]);
      } else if (isCurrentUser(userId, currentUser)) {
        return dispatch(setUserId(userId));
      }
      return Promise.resolve({});
    });
  }

  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);

  if (!canFetchData) {
    return Promise.resolve();
  }

  return Promise.all([
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
    dispatch(showUser(userId, config)),
    dispatch(
      queryUserListings({
        params: queryParams,
        userId,
        config,
        currentPathParams: params,
      })
    ),
    dispatch(queryUserReviews(userId)),
    dispatch(fetchSoldItemsUsingIsdk(userId?.uuid)),
  ]);
};
